import { z } from "zod";
import { Uuid } from "@secretaa/http-contract";
import { Identity, Ref, Key, Label, Effect, Digest, sameRef, sameScope } from "./primitives.ts";
import { PlanNode } from "./graph.ts";
import { BridgeSpec, AIProcessingSpec } from "./bridge.ts";

/** A bounded semantic contract. No executable JSON schema, expressions or remote refs. */
export const OperationShape = z.object({
  ref: Ref, encoding:z.enum(["OBJECT","QUERY_RESULT"]), cardinality: z.enum(["ONE", "LIST"]), maxItems: z.number().int().min(1).max(10000),
  fields: z.array(z.object({ key: Key, type: z.enum(["text", "number", "money", "date", "bool"]),
    nullable: z.boolean(), unit: z.string().min(1).max(40).nullable(),
  }).strict()).max(32),
}).strict().superRefine((v,c)=>{
  if(new Set(v.fields.map(f=>f.key)).size!==v.fields.length || (v.cardinality==="ONE"&&v.maxItems!==1))c.addIssue({code:"custom",message:"OPERATION_SHAPE_INVALID"});
});
const base={...Identity,label:Label.optional(),dataPolicyRef:Ref,input:OperationShape,output:OperationShape};
/** Canonical definitions owned by Data. CAPABILITY_CALL stores only an installation and
 * opaque binding refs; its definition always comes from the Platform catalog owner. */
export const PlanningOperationDefinition=z.discriminatedUnion("kind",[
  z.object({...base,kind:z.literal("TRANSFORM"),bridge:BridgeSpec}).strict(),
  z.object({...base,kind:z.literal("AI_PROCESS"),processing:AIProcessingSpec,
    instruction:z.object({ref:Ref,text:z.string().trim().min(1).max(8000)}).strict(),
    rubric:z.object({ref:Ref,criteria:z.array(z.string().trim().min(1).max(500)).min(1).max(16)}).strict(),
    profile:z.object({ref:Ref,key:z.enum(["process.extract","process.semantic"]),corePolicyRef:Ref}).strict(),
  }).strict(),
  z.object({...base,kind:z.literal("DATA_TASK"),effect:z.literal("DATA_WRITE"),
    operation:z.literal("CREATE_RECORD"),collectionId:Uuid,manifestRef:Ref,manifestHash:Digest,
    fields:z.array(z.object({inputKey:Key,fieldId:Uuid}).strict()).min(1).max(32),maxRecords:z.literal(1),
    transaction:z.literal("LOCAL_DATA_ONLY"),
  }).strict(),
  z.object({...base,kind:z.literal("SUBFLOW"),effect:Effect,
    nodes:z.array(PlanNode).min(1).max(16),resultNodeId:Key,
    childPolicy:z.object({ref:Ref,maxDepth:z.number().int().min(1).max(2),maxChildren:z.number().int().min(1).max(4),
      cancellation:z.literal("PROPAGATE"),budget:z.literal("SHARED_REQUEST"),deadlineMs:z.number().int().min(1).max(60000),
    }).strict(),
  }).strict(),
  z.object({...base,kind:z.literal("CAPABILITY_CALL"),capabilityRef:Ref,targetConstraintRef:Ref,
    bindingRef:Ref,installationRevision:z.number().int().positive().max(2147483647),
  }).strict(),
]).superRefine((v,c)=>{
  const issue=(message:string)=>c.addIssue({code:"custom",message});
  if(!v.scope.projectId)issue("OPERATION_PROJECT_REQUIRED");
  if(v.kind==="TRANSFORM"){
    if(!sameRef(v.bridge,v)||!sameScope(v.bridge.scope,v.scope)||!sameRef(v.bridge.sourceContractRef,v.input.ref)||!sameRef(v.bridge.targetContractRef,v.output.ref))issue("BRIDGE_DEFINITION_MISMATCH");
  }
  if(v.kind==="AI_PROCESS"){
    const p=v.processing;
    if(!sameRef(p,v)||!sameScope(p.scope,v.scope)||!sameRef(p.inputContractRef,v.input.ref)||!sameRef(p.outputContractRef,v.output.ref)||!sameRef(p.dataPolicyRef,v.dataPolicyRef)
      ||!sameRef(p.instructionRef,v.instruction.ref)||!sameRef(p.rubricRef,v.rubric.ref)||!sameRef(p.modelProfileRef,v.profile.ref))issue("PROCESSING_DEFINITION_MISMATCH");
  }
  if(v.kind==="DATA_TASK"&&(v.input.cardinality!=="ONE"||new Set(v.fields.map(f=>f.fieldId)).size!==v.fields.length||new Set(v.fields.map(f=>f.inputKey)).size!==v.fields.length||v.fields.some(f=>!v.input.fields.some(i=>i.key===f.inputKey))))issue("TASK_BINDING_INVALID");
  if(v.kind==="SUBFLOW"&&(!v.nodes.some(n=>n.id===v.resultNodeId&&sameRef(n.outputContractRef,v.output.ref))||new Set(v.nodes.map(n=>n.id)).size!==v.nodes.length))issue("FLOW_RESULT_INVALID");
});
export type PlanningOperationDefinition=z.infer<typeof PlanningOperationDefinition>;
export type OperationShape=z.infer<typeof OperationShape>;
export const PlanningOperationRef=z.object({kind:z.enum(["TRANSFORM","AI_PROCESS","SUBFLOW","DATA_TASK","CAPABILITY_CALL"]),ref:Ref}).strict();
export type PlanningOperationRef=z.infer<typeof PlanningOperationRef>;
export const PlanningOperationRefs=z.array(PlanningOperationRef).max(8).refine(v=>new Set(v.map(x=>`${x.kind}:${x.ref.id}:${x.ref.version}`)).size===v.length,"DUPLICATE_OPERATION");
export const PlanningOperationDefinitionContractRef=Object.freeze({id:"85300000-0000-4000-8000-000000000008",version:1});
export function operationNodeRef(node:z.infer<typeof PlanNode>):z.infer<typeof PlanningOperationRef>|null{
  switch(node.kind){
    case "TRANSFORM":return {kind:node.kind,ref:node.bridgeRef};
    case "AI_PROCESS":return {kind:node.kind,ref:node.processingRef};
    case "SUBFLOW":return {kind:node.kind,ref:node.flowRef};
    case "DATA_TASK":return {kind:node.kind,ref:node.taskRef};
    case "CAPABILITY_CALL":return {kind:node.kind,ref:node.capabilityRef};
    default:return null;
  }
}

/** Selected model metadata, not a copy of instruction/rubric/child graph or proof. The
 * digest commits to the full canonical definition, which the owner rehydrates. */
export const PlanningOperationFact=z.object({
 id:Uuid,version:Ref.shape.version,kind:PlanningOperationRef.shape.kind,definitionDigest:Digest,
 input:OperationShape,output:OperationShape,effect:Effect,
 childPolicyRef:Ref.optional(),targetConstraintRef:Ref.optional(),purpose:z.string().trim().min(1).max(8000).optional(),label:Label.optional(),
}).strict().superRefine((v,c)=>{
 if((v.kind==="SUBFLOW")!==!!v.childPolicyRef||(v.kind==="CAPABILITY_CALL")!==!!v.targetConstraintRef)c.addIssue({code:"custom",message:"OPERATION_FACT_REFS_INVALID"});
});
export type PlanningOperationFact=z.infer<typeof PlanningOperationFact>;
